import { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { every, find, isNil, max, orderBy, some } from 'lodash';
import type { MutableRefObject } from 'react';

import { ApiDateBounds, dateRangeParams } from '@/api/historicalData/helpers';
import { AssembledSeries, nextAssembledSeries } from '@/api/liveSplitQuery/assembledSeries';
import { clippedSeriesCache } from '@/api/liveSplitQuery/clippedSeriesCache';
import { PlaceholderCacheKey, placeholderCache } from '@/api/liveSplitQuery/placeholderCache';
import { stitchedSeriesCache } from '@/api/liveSplitQuery/stitchedSeriesCache';
import { DateRangeRequestParams, FetchRangeParams, LiveSplitOptionsBase } from '@/api/liveSplitQuery/types';
import { CombinedQueryResult, PENDING_STATUS, QueryStatusFields, rollUpStatus } from '@/common/react-query';
import { DateRangeQueryKeyPrefix, QUERIES_REFETCH_INTERVAL, dateRangeQueryKey } from '@/config';
import { WavelinkQueryKey } from '@/hooks/useWavelink';
import { DateRange } from '@/store';
import {
    DataWindow,
    SENSOR_DATA_GC_TIME_MS,
    SETTLED_CHUNK_RETRY,
    SortOrder,
    newestTimestampOf,
    sensorDataStaleTime,
    toDataWindow,
} from '@/utils';

/**
 * Where each spotter's windows sit in the flat result list, and the bounds they are read under.
 *
 * `buildWindowQueries` generates its query list from this layout, and the same layout must be used to
 * match the results back to the correct spotter.
 */
export type SpotterWindowLayout<TTimestampKey extends string> = {
    spotterIds: string[] | null | undefined;
    /** The settled half, already cut into the chunks that hold their own cache keys. */
    settledChunks: DateRange[];
    liveDateRange: DateRange | undefined;
    /** One placeholder cache key per spotter, in `spotterIds` order. */
    cacheKeys: PlaceholderCacheKey[];
    startMs: number | undefined;
    endMs: number | undefined;
    /**
     * How far the windows actually reach, which is past the caller's `endDate` while the live half
     * overshoots to the next hour.
     */
    windowEndMs: number | undefined;
    /** The field both date bounds filter on server side, and therefore where the windows are cut. */
    timestampKey: TTimestampKey;
    sortOrder: SortOrder;
};

export type BuildWindowQueriesParams<TItem extends Record<TTimestampKey, string>, TTimestampKey extends string> = {
    layout: SpotterWindowLayout<TTimestampKey>;
    queryKeyPrefix: DateRangeQueryKeyPrefix;
    keyOptions: Record<string, unknown> | undefined;
    viewToken: string | null;
    isPublic: boolean;
    /** Answers whether wavelink pushes invalidations for a key, in which case that window is not polled. */
    isQueryCovered: (queryKey: WavelinkQueryKey) => boolean;
    isEnabled: boolean;
    apiDateBounds: ApiDateBounds | undefined;
    responseSortOrder: SortOrder | 'unsorted';
    /**
     * The caller's own query options. `enabled` is not among them: the live window's own `enabled` carries
     * the guard that `liveFetchParams` is non-null, so an override would fetch with no bounds at all.
     */
    config: Omit<NonNullable<LiveSplitOptionsBase<TItem, TTimestampKey>['config']>, 'enabled'> | undefined;
    /**
     * A ref rather than the function, so a window built under an earlier render still calls the newest
     * one the caller passed. Callers construct this inline, so its identity says nothing about whether
     * what it fetches has changed.
     */
    fetchRangeRef: MutableRefObject<(params: FetchRangeParams) => Promise<TItem[]>>;
};

/**
 * Builds one flat list of queries: for each spotter the settled chunks in `settledChunks` order, then
 * that spotter's live window. One list lets `useQueries` combine every result in one `combine` function,
 * so react-query owns the result object's stability. Every spotter has the same chunk count, because
 * they share the range, which is what lets `combineSpotterWindows` slice the results on one stride.
 */
export const buildWindowQueries = <TItem extends Record<TTimestampKey, string>, TTimestampKey extends string>({
    layout,
    queryKeyPrefix,
    keyOptions,
    viewToken,
    isPublic,
    isQueryCovered,
    isEnabled,
    apiDateBounds,
    responseSortOrder,
    config,
    fetchRangeRef,
}: BuildWindowQueriesParams<TItem, TTimestampKey>): UseQueryOptions<TItem[]>[] => {
    type SubQueryOptions = UseQueryOptions<TItem[]>;

    const { spotterIds, settledChunks, liveDateRange, timestampKey, sortOrder } = layout;

    /* Everything the key and the request take from the window rather than from the spotter. */
    const settledKeyOptions = { isPublic, ...keyOptions };
    const liveKeyOptions = { ...settledKeyOptions, live: true };
    const chunkKeyParams = settledChunks.map((chunk) => dateRangeParams(chunk));
    const chunkFetchParams = settledChunks.map((chunk) => dateRangeParams(chunk, apiDateBounds)!);
    const liveKeyParams = dateRangeParams(liveDateRange);
    const liveFetchParams = dateRangeParams(liveDateRange, apiDateBounds);

    return (spotterIds ?? []).flatMap((spotterId): SubQueryOptions[] => {
        /** When wavelink covers this data it pushes invalidations, and we must not also poll. */
        const isCoveredByWavelink = isQueryCovered([queryKeyPrefix, spotterId]);

        const fetchInSortOrder = async (rangeParams: DateRangeRequestParams, signal: AbortSignal) => {
            const items = await fetchRangeRef.current({
                spotterId,
                ...rangeParams,
                viewToken,
                signal,
            });
            /*
             * Ordered here rather than in a memo so it happens once per fetch and react-query caches the result:
             * data carried forward as placeholder across a key change is then already in order too
             */
            return responseSortOrder === sortOrder ? items : orderBy(items, [timestampKey], [sortOrder]);
        };

        const settledQueries = settledChunks.map(
            (chunk, chunkIndex): SubQueryOptions => ({
                queryKey: dateRangeQueryKey(queryKeyPrefix, spotterId, chunkKeyParams[chunkIndex], settledKeyOptions),
                queryFn: ({ signal }) => fetchInSortOrder(chunkFetchParams[chunkIndex], signal),
                staleTime: sensorDataStaleTime(chunk),
                gcTime: SENSOR_DATA_GC_TIME_MS,
                // a settled chunk cannot gain data, so it is never polled and never invalidated by wavelink
                refetchInterval: false,
                retry: SETTLED_CHUNK_RETRY,
                enabled: isEnabled,
                ...config,
            })
        );

        const liveQuery: SubQueryOptions = {
            queryKey: dateRangeQueryKey(queryKeyPrefix, spotterId, liveKeyParams, liveKeyOptions),
            queryFn: ({ signal }) => fetchInSortOrder(liveFetchParams!, signal),
            staleTime: sensorDataStaleTime(liveDateRange),
            gcTime: SENSOR_DATA_GC_TIME_MS,
            refetchInterval: isCoveredByWavelink ? false : QUERIES_REFETCH_INTERVAL,
            enabled: isEnabled && !isNil(liveDateRange),
            ...config,
        };

        return [...settledQueries, liveQuery];
    });
};

export type CombinedSpotterWindows<TItem> = {
    status: Omit<CombinedQueryResult<(TItem[] | undefined)[]>, 'data'>;
    series: AssembledSeries<TItem>;
    newestTimestamp: number | undefined;
};

/**
 * Every window one spotter's series is built from, in ascending time order, or `undefined` while any
 * chunk is still missing.
 *
 * The live window is an up to two hour payload racing the settled chunks, so it very well could arrive
 * first. Holding it back keeps `data` in step with the status fields, which come from the chunks:
 * otherwise callers that gate on data rather than `isPending` draw once with two hours of data and again
 * with the whole window.
 */
const collectWindows = <TItem>(
    settledChunks: DateRange[],
    settledData: (TItem[] | undefined)[],
    liveDateRange: DateRange | undefined,
    liveData: TItem[] | undefined
): DataWindow<TItem>[] | undefined => {
    const liveWindow = toDataWindow(liveDateRange, liveData);
    if (!settledChunks.length) {
        return liveWindow && [liveWindow];
    }

    const chunkWindows: DataWindow<TItem>[] = [];
    for (let chunkIndex = 0; chunkIndex < settledChunks.length; chunkIndex++) {
        const chunkWindow = toDataWindow(settledChunks[chunkIndex], settledData[chunkIndex]);
        if (!chunkWindow) {
            return undefined;
        }
        chunkWindows.push(chunkWindow);
    }

    // an empty live answer contributes nothing, and must not stretch the covered end over it
    return liveWindow?.data.length ? [...chunkWindows, liveWindow] : chunkWindows;
};

/**
 * One spotter's series and its own verdict, which `rollUpStatus` reads to reach the whole result's.
 *
 * Rolling each spotter up before the whole result is what keeps `isPlaceholderData` honest: it spans
 * every window of every spotter, so a spotter standing in with a carried-forward window reports the
 * whole result as placeholder data rather than as fresh.
 */
type SpotterState<TItem> = QueryStatusFields & {
    data: TItem[] | undefined;
    /** What was assembled and the bounds it actually covers, or `undefined` while nothing fresh has landed. */
    assembled: DataWindow<TItem> | undefined;
};

/**
 * Slices the flat result list into one series per spotter, stitching each spotter's windows together and
 * clipping the result to the range that was asked for.
 *
 * `held` is the series handed out last. It is returned unchanged when every array behind it is, so an
 * observer with nothing new does not re-render.
 */
export const combineSpotterWindows = <TItem extends Record<TTimestampKey, string>, TTimestampKey extends string>(
    results: UseQueryResult<TItem[]>[],
    layout: SpotterWindowLayout<TTimestampKey>,
    held: AssembledSeries<TItem> | undefined
): CombinedSpotterWindows<TItem> => {
    const {
        spotterIds,
        settledChunks,
        liveDateRange,
        cacheKeys,
        startMs,
        endMs,
        windowEndMs,
        timestampKey,
        sortOrder,
    } = layout;

    // Each spotter's settled chunks, then its live window: see `buildWindowQueries`.
    const chunkCount = settledChunks.length;
    const windowsPerSpotter = chunkCount + 1;

    let newestTimestamp: number | undefined;

    const spotterStates = (spotterIds ?? []).map((_spotterId, index): SpotterState<TItem> => {
        const spotterResults = results.slice(index * windowsPerSpotter, (index + 1) * windowsPerSpotter);
        const settledResults = spotterResults.slice(0, chunkCount);
        const liveResult = spotterResults[chunkCount];

        /*
         * The settled chunks own the verdict when present: their failure is permanent, while the
         * polled live window can recover. Reading both would also leave a range with no settled part
         * pending forever, because the chunk queries are disabled.
         */
        const primary = chunkCount ? settledResults : [liveResult];

        const windows = collectWindows(
            settledChunks,
            settledResults.map((result) => result?.data),
            liveDateRange,
            liveResult?.data
        );

        const freshData = windows && stitchedSeriesCache.stitch(windows, timestampKey, sortOrder);

        /* A placeholder has to actually show something, which is what `forRange` decides. */
        const previous = isNil(freshData)
            ? placeholderCache.forRange<TItem>(cacheKeys[index], startMs, endMs)
            : undefined;
        const isPlaceholderData = !isNil(previous);
        const assembledData = freshData ?? previous?.data;

        if (!isNil(freshData)) {
            /*
             * Read off the assembled series rather than the clipped one below, so an observation
             * arriving in the live overshoot is still reported. That is the whole point of asking
             * for the overshoot.
             */
            const latestTimestamp = newestTimestampOf(freshData, timestampKey, sortOrder);
            if (!isNil(latestTimestamp)) {
                newestTimestamp = max([newestTimestamp, latestTimestamp]);
            }
        }

        /*
         * Everything handed to a caller spans the range it asked for, out to however far the live
         * half overshoots. The windows are widened to the hour so their keys hold still, and a
         * carried forward window covers whatever range it was assembled under, so both can reach
         * further back than asked for. Nothing downstream bounds a chart to its own axis: the
         * charts set their x domain from the range and draw whatever else they are given outside it.
         */
        const spotterData =
            assembledData && clippedSeriesCache.clip(assembledData, timestampKey, startMs, windowEndMs ?? endMs);

        const isError = some(primary, 'isError');
        /* carried forward data stands in for the answer, the same way a placeholder does */
        const isPending = !isPlaceholderData && some(primary, 'isPending');
        const isSuccess = !isError && (isPlaceholderData || every(primary, 'isSuccess'));

        return {
            data: spotterData,
            isPlaceholderData,
            /*
             * The bounds reported are the ones the windows actually cover, which stop short of the live
             * end while the live window is still missing or empty.
             */
            assembled:
                windows && !isNil(freshData)
                    ? { data: freshData, startMs: windows[0].startMs, endMs: windows[windows.length - 1].endMs }
                    : undefined,
            isError,
            isPending,
            isLoading: !isPlaceholderData && some(primary, 'isLoading'),
            isSuccess,
            status: isSuccess ? 'success' : isPending ? 'pending' : 'error',
            /* Every window this spotter asked for, so a live refetch behind settled chunks still reports. */
            isFetching: some(spotterResults, 'isFetching'),
            error: find(primary, 'error')?.error ?? null,
        };
    });

    return {
        status: spotterIds ? rollUpStatus(spotterStates) : PENDING_STATUS,
        series: nextAssembledSeries(
            held,
            spotterStates.map((spotter) => spotter.data),
            spotterStates.map((spotter) => spotter.assembled)
        ),
        newestTimestamp,
    };
};
